import { z } from 'zod';
import { baseEntityFields, idSchema, isoDateTimeSchema, propertyExpressionStringSchema, } from './common.schema';
/**
 * ScanJob — scheduled network discovery sweep (PRD §2.2, LM NetScan
 * equivalent). A collector executes the sweep and posts a ScanReport to
 * the ingest surface; auto-add rules decide which responders become
 * resources.
 */
const cidrSchema = z
    .string()
    .regex(/^((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\/([0-9]|[12]\d|3[0-2])$/, 'must be IPv4 CIDR notation (e.g. 10.0.0.0/24)')
    // /16 = 65,536 addresses — matches the ScanReport results cap; wider
    // sweeps must be split into multiple jobs.
    .refine((cidr) => parseInt(cidr.split('/')[1], 10) >= 16, {
    message: 'prefix must be /16 or narrower',
});
export const scanJobInputSchema = z
    .object({
    name: z.string().min(1).max(200),
    collectorId: idSchema,
    cidrRanges: z.array(cidrSchema).min(1).max(20),
    /** Re-scan interval in hours; null = manual runs only. */
    intervalHours: z.number().int().min(1).max(720).nullable().default(null),
    /** Auto-add responders as resources. */
    autoAdd: z.boolean().default(false),
    /** Property expression over scan fingerprints (system.* keys set by
     *  classification) gating auto-add; null = add every responder. */
    autoAddFilter: propertyExpressionStringSchema.nullable().default(null),
    /** Client the auto-added resources belong to. */
    clientId: idSchema.nullable().default(null),
    enabled: z.boolean().default(true),
})
    .strict()
    .superRefine((val, ctx) => scanJobPolicyRules(val, ctx));
/** autoAddFilter is meaningless without autoAdd — reject the latent
 *  state on inputs AND stored docs (update merges re-validate here). */
export function scanJobPolicyRules(val, ctx) {
    if (!val.autoAdd && val.autoAddFilter) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['autoAddFilter'],
            message: 'autoAddFilter requires autoAdd',
        });
    }
}
/** Partial patches; the cross-field policy re-runs on the MERGED doc
 *  via scanJobSchema at write time. (Built from the pre-effects object
 *  shape — ZodEffects cannot .partial().) */
export const scanJobUpdateSchema = scanJobInputSchema.innerType().partial().strict();
export const scanJobSchema = z
    .object({
    name: z.string().min(1).max(200),
    collectorId: idSchema,
    cidrRanges: z.array(cidrSchema).min(1).max(20),
    intervalHours: z.number().int().min(1).max(720).nullable(),
    autoAdd: z.boolean(),
    autoAddFilter: propertyExpressionStringSchema.nullable(),
    clientId: idSchema.nullable(),
    enabled: z.boolean(),
    lastRunAt: isoDateTimeSchema.nullable(),
    /** Set while a run is outstanding on the collector. */
    runRequestedAt: isoDateTimeSchema.nullable(),
})
    .extend(baseEntityFields)
    .strict()
    .superRefine((val, ctx) => scanJobPolicyRules(val, ctx));
//# sourceMappingURL=scan-job.schema.js.map